<script setup lang="ts">
import type { AwardConfig } from '~/types/appTypes'

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()
const storeMuseumChar = useStoreMuseumChar()

definePageMeta({ layout: 'layout-side-default' })

const awardConfigList = useAppConfig().app.award
const awardConfig = awardConfigList[route.params.award as string] as AwardConfig

const year = app.$anyToInteger(route.params.year)
const slug = route.params.slug as string

const pageData = {
  charItem: null as MuseumCharItem | null,
  items: null as CommentArticle[] | null,
}

let headTitle = ''
const setHead = () => {
  const baseTitle = awardConfig.baseTitle
  const breadcrumbTitle = awardConfig.breadcrumbTitle || baseTitle
  headTitle = `${baseTitle}${year}`
  const headDescription = awardConfig.description
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: breadcrumbTitle,
      disabled: false,
      to: {
        name: 'award',
        params: { award: awardConfig.awardSlug },
      },
    },
    {
      text: headTitle,
      disabled: false,
      to: {
        name: 'award-year',
        params: {
          award: awardConfig.awardSlug,
          year: year,
        },
      },
    },
    {
      text: pageData.charItem.title,
      disabled: false,
      to: {
        name: 'award-year-slug',
        params: {
          award: awardConfig.awardSlug,
          year: year,
          slug: slug,
        },
      },
    },
    {
      text: '応援メッセージ',
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const initPage = async () => {
  let res
  const items = []

  const [_resSite, _resMaster, resComment, charaItems] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchList('awardComment', {
      config: {
        articleTypeSlug: awardConfig.awardSlug,
        commentSlug: slug,
      },
    }),
    storeMuseumChar.fetchCharaList(kijidasConfig, awardConfig.awardSlug, year, 0, slug),
  ])
  if (resComment.code !== 200000) {
    throw createError({
      statusCode: 999,
      statusMessage: '応援メッセージ読み込みエラー',
    })
    return
  }
  if (!charaItems?.length) {
    throw createError({ statusCode: 404 })
    return
  }

  // コメント空白トリム＆除外
  const rawItems = resComment.data.articles as CommentArticle[]
  for (let i = 0; i < rawItems.length; i++) {
    const item = rawItems[i]
    if (item.comment) {
      if (item.comment.trim()) {
        items.push(item)
      }
    }
  }

  pageData.items = items
  pageData.charItem = charaItems[0]
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articlePage">
    <h1 class="c-heading_page">
      {{ headTitle }}
    </h1>

    <div class="p-museumChar_chara">
      <nuxt-link
        class="p-museumChar_chara_image"
        v-bind:to="pageData.charItem.toDetail"
      >
        <CommonImage
          v-bind:src="pageData.charItem.image.url"
          v-bind:alt="pageData.charItem.title"
          object-fit="contain"
        />
      </nuxt-link>
      <div class="p-museumChar_chara_body">
        <div class="p-museumChar_chara_body_title">
          {{ pageData.charItem.title }}
        </div>
        <div
          v-if="pageData.charItem.museum"
          class="p-museumChar_chara_body_museum"
        >
          <nuxt-link
            v-bind:to="pageData.charItem.toMuseum"
          >
            {{ pageData.charItem.museum }}
          </nuxt-link>
        </div>
        <div
          class="p-museumChar_chara_body_count"
        >
          投票数：<span>{{ app.$anyToComma(pageData.charItem.articleCommentCount) }}</span> 票
        </div>
      </div>
    </div>

    <div
      class="p-articlePage_result"
    >
      <div class="p-museumChar_head">
        <img
          class="p-museumChar_head_icon"
          src="@/assets/images/icon-title.png"
        >
        <span
          class="p-museumChar_head_title"
        >
          応援メッセージ
        </span>
      </div>
      <div class="p-articlePage_list">
        <pre
          v-for="item in pageData.items"
          v-bind:key="item.id"
          class="p-articlePage_item"
        >{{ item.comment }}</pre>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

#staticBody {
  :deep(> *:not(table)) {
    display: none;
  }

  :deep(> table) {
    width: 100%;

    > tbody {
      tr:first-child,
      tr:nth-child(2),
      tr:last-child {
        display: none;
      }
    }
  }
}

.p-articlePage {
  &_item {
    width: 100%;
    padding: 10px 0 10px 1em;
    border-bottom: 1px solid #ebebeb;
    color: var(--color_text_base);
    font-size: 16px;
    line-height: 1.7;
    white-space: pre-wrap;

    @include responsive.mq_down(sp) {
      padding: 5px 0 5px 5px;
      font-size: 14px;
    }

    &::before {
      content: "\276F ";
      display: inline-block;
      width: 1em;
      margin-left: -1em;
      color: var(--color_text_base);
      font-size: 18px;

      @include responsive.mq_down(sp) {
        font-size: 16px;
      }
    }
  }
}

.p-museumChar {
  &_chara {
    display: flex;
    margin: 40px 0;

    @include responsive.mq_down(sp) {
      display: block;
    }

    &_image {
      flex-shrink: 0;
      width: 200px;

      @include responsive.mq_down(sp) {
        display: block;
        width: 100%;
        max-width: 250px;
        margin: 0 auto;
      }
    }

    &_body {
      flex-grow: 1;
      margin-left: 20px;
      padding: 20px;
      background-color: var(--color_background_whiteblue);

      @include responsive.mq_down(sp) {
        margin-left: 0;
        padding: 15px;
      }

      &_title {
        font-size: 20px;
        font-weight: bold;

        @include responsive.mq_down(sp) {
          font-size: 18px;
        }
      }

      &_museum {
        font-size: 16px;

        @include responsive.mq_down(sp) {
          font-size: 14px;
        }
      }

      &_count {
        margin-top: 20px;
        font-size: 18px;

        @include responsive.mq_down(sp) {
          font-size: 16px;
        }

        > span {
          font-size: 26px;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            font-size: 20px;
          }
        }
      }
    }
  }

  &_head {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    padding-bottom: 12px;
    border-bottom: solid 1px var(--g_color_border_gray);

    &_icon {
      margin-top: 3px;
      margin-right: 15px;

      @include responsive.mq_down(sp) {
        width: 20px;
        height: 20px;
      }
    }

    &_title {
      display: inline-block;
      position: relative;
      width: 100%;
      margin-right: 10px;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 20px;
      font-weight: bold;
      line-height: 1.5;

      @include responsive.mq_down(sp) {
        font-size: 18px;
      }
    }
  }
}
</style>
